import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { CheckCircle2, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Retry function with exponential backoff
  const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
    let lastError;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fn();
      } catch (err) {
        lastError = err;
        // Don't retry on validation errors (4xx)
        if (err.response && err.response.status >= 400 && err.response.status < 500) {
          throw err;
        }
        // Wait with exponential backoff before retrying
        if (attempt < maxRetries - 1) {
          const delay = baseDelay * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    throw lastError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Use retry with exponential backoff for server errors
      await retryWithBackoff(() => axios.post(`${API}/contact`, formData));
      setSubmitSuccess(true);
      
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
        setSubmitSuccess(false);
      }, 5000);
    } catch (err) {
      // More user-friendly error messages
      if (err.code === 'ERR_NETWORK' || !err.response) {
        setError('Connection error. Please check your internet and try again.');
      } else if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else if (err.response?.status >= 500) {
        setError('Server is temporarily unavailable. Please try again in a few moments.');
      } else {
        setError(err.response?.data?.detail || 'Failed to send message. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 text-center animate-fade-in-up">
        <div className="flex justify-center mb-4">
          <CheckCircle2 className="w-16 h-16 text-green-500" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-secondary mb-3">Message Sent!</h3>
        <p className="text-muted-foreground">Thank you for contacting us. We'll get back to you shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 md:p-8">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Your Name
          </Label>
          <Input
            id="name"
            name="name"
            data-testid="contact-name-input"
            placeholder="John Smith"
            value={formData.name}
            onChange={handleInputChange}
            required
            className="h-11"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Phone (Optional)
          </Label>
          <Input
            id="phone"
            name="phone"
            data-testid="contact-phone-input"
            type="tel"
            placeholder="0412 345 678"
            value={formData.phone}
            onChange={handleInputChange}
            className="h-11"
          />
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <Label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Email Address
        </Label>
        <Input
          id="email"
          name="email"
          data-testid="contact-email-input"
          type="email"
          placeholder="john@example.com"
          value={formData.email}
          onChange={handleInputChange}
          required
          className="h-11"
        />
      </div>

      <div className="space-y-2 mb-4">
        <Label htmlFor="subject" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Subject
        </Label>
        <Input
          id="subject"
          name="subject"
          data-testid="contact-subject-input"
          placeholder="How can we help?"
          value={formData.subject}
          onChange={handleInputChange}
          required
          className="h-11"
        />
      </div>

      <div className="space-y-2 mb-6">
        <Label htmlFor="message" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Message
        </Label>
        <Textarea
          id="message"
          name="message"
          data-testid="contact-message-input"
          placeholder="Tell us more about your inquiry..."
          value={formData.message}
          onChange={handleInputChange}
          required
          rows={5}
          className="resize-none"
        />
      </div>

      <Button
        type="submit"
        data-testid="contact-submit"
        disabled={isSubmitting}
        className="w-full h-12 bg-primary hover:bg-primary-hover text-white font-bold text-lg tracking-wide rounded-lg transition-colors duration-200"
      >
        {isSubmitting ? 'Sending...' : (
          <>
            <Send className="w-5 h-5 mr-2" />
            Send Message
          </>
        )}
      </Button>
    </form>
  );
};

export default ContactForm;
